import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { Address, formatUnits } from 'viem'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { BORROW_VAULT_CHAIN_ID } from '../../eborrow/logic/useVaultList'
import { getNativeApyForAsset, useNativeApys } from './useNativeApys'
import { useTokensInfo, getTokenFromMap } from '@/domain/euler'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { useEulerIndexerELends } from '@/domain/euler/elends/elendsQuery'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useAvailableSubAccount } from '@/domain/euler/positions/useAvailableSubAccount'
import { useAccountLiquidity } from '@/domain/euler/positions/useAccountLiquidity'
import { decodeEulerCapToBigInt } from '@/domain/euler/shared/caps'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { getEntities } from '@/features/evault-details/data/entities'
import { selectGovernorsFromAddresses } from '@/features/evault-details/logic/selectGovernors'

interface UseBorrowDetailsParams {
  collateral: Address
  liability: Address
  /** Specific sub-account ID to query. If not provided, uses the first found position. */
  subAccountId?: number | null
}

// Helper to check if two assets are correlated (same base asset type)
function isCorrelatedAssets(symbol1?: string, symbol2?: string): boolean {
  if (!symbol1 || !symbol2) return false
  const normalizedSymbol1 = symbol1.toLowerCase().replace(/[^a-z]/g, '')
  const normalizedSymbol2 = symbol2.toLowerCase().replace(/[^a-z]/g, '')

  // Check for common correlated pairs (ETH/WETH, stablecoins, HYPE variants, etc.)
  const stablecoins = ['usdc', 'usdt', 'usdh', 'usdt', 'dai', 'frax', 'usde', 'usd']
  const ethVariants = ['eth', 'weth', 'steth', 'cbeth', 'reth', 'wsteth', 'ueth']
  const btcVariants = ['btc', 'wbtc', 'tbtc', 'cbtc', 'ubtc']
  const hypeVariants = ['hype', 'whype', 'khype', 'wsthype', 'lsthype', 'behype']

  const isInGroup = (symbol: string, group: string[]) => group.some((g) => symbol.includes(g))

  if (isInGroup(normalizedSymbol1, stablecoins) && isInGroup(normalizedSymbol2, stablecoins)) return true
  if (isInGroup(normalizedSymbol1, ethVariants) && isInGroup(normalizedSymbol2, ethVariants)) return true
  if (isInGroup(normalizedSymbol1, btcVariants) && isInGroup(normalizedSymbol2, btcVariants)) return true
  if (isInGroup(normalizedSymbol1, hypeVariants) && isInGroup(normalizedSymbol2, hypeVariants)) return true

  return false
}

export function useBorrowDetails({ collateral, liability, subAccountId: propSubAccountId }: UseBorrowDetailsParams) {
  const { address: userAddress } = useAccount()

  // Find if user has an existing position on a sub-account
  const {
    existingSubAccountId,
    hasExistingPosition,
    isLoading: isSubAccountLoading,
  } = useAvailableSubAccount({
    liabilityVault: liability,
    userAddress,
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: !!userAddress,
  })

  // Use the provided sub-account ID, or fall back to the first found position
  const activeSubAccountId =
    propSubAccountId !== undefined ? propSubAccountId : hasExistingPosition ? existingSubAccountId : null

  // Use the sub-account address for querying position data (if user has a position)
  // Otherwise use main address for backward compatibility
  const positionAddress =
    activeSubAccountId !== null && userAddress ? getSubAccountAddress(userAddress, activeSubAccountId) : userAddress

  // Fetch account-wide liquidity info from the Euler Account Lens
  // This provides accurate health factor and LTV calculations considering ALL enabled collaterals
  const { data: accountLiquidity, isLoading: isAccountLiquidityLoading } = useAccountLiquidity({
    accountAddress: positionAddress,
    liabilityVault: liability,
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: !!positionAddress && !!liability,
  })

  // Fetch underlying assets for both vaults
  const { data: vaultAssetsData, isLoading: isAssetsLoading } = useReadContracts({
    contracts: [
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'asset',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'asset',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'oracle',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'unitOfAccount',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'EVC',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'governorAdmin',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'governorAdmin',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled: !!collateral && !!liability,
    },
  })

  const collateralUnderlying = vaultAssetsData?.[0]?.result as Address | undefined
  const liabilityUnderlying = vaultAssetsData?.[1]?.result as Address | undefined
  const oracle = vaultAssetsData?.[2]?.result as Address | undefined
  const unitOfAccount = vaultAssetsData?.[3]?.result as Address | undefined
  const evcAddress = vaultAssetsData?.[4]?.result as Address | undefined
  const collateralGovernor = vaultAssetsData?.[5]?.result as Address | undefined
  const liabilityGovernor = vaultAssetsData?.[6]?.result as Address | undefined

  // Fetch native APY (intrinsic yield for PT tokens, staked assets, etc.)
  const { data: nativeApysData, isLoading: isNativeApyLoading } = useNativeApys({
    chainId: BORROW_VAULT_CHAIN_ID,
  })

  // Collect underlying addresses for Euler indexer lookup
  const underlyingAddresses = [collateralUnderlying, liabilityUnderlying].filter((addr): addr is Address => !!addr)

  // Fetch token info from Euler indexer (with onchain fallback)
  const { tokens: indexerTokens, isPending: isTokenLoading } = useTokensInfo(BORROW_VAULT_CHAIN_ID, underlyingAddresses)

  // Fetch indexer vault data for products lookup
  const { data: indexerVaults } = useEulerIndexerELends(BORROW_VAULT_CHAIN_ID)

  // Get token info from indexer
  const collateralTokenInfo = getTokenFromMap(indexerTokens, collateralUnderlying)
  const liabilityTokenInfo = getTokenFromMap(indexerTokens, liabilityUnderlying)

  // Fetch LTV info for this collateral-liability pair
  const { data: ltvInfo, isLoading: isLtvLoading } = useReadContract({
    address: liability,
    abi: eVaultImplementationAbi,
    functionName: 'LTVFull',
    args: [collateral],
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!collateral && !!liability,
    },
  })

  // Fetch vault stats for liability vault
  const { data: liabilityStats, isLoading: isLiabilityStatsLoading } = useReadContracts({
    contracts: [
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'totalAssets',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'totalBorrows',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'interestRate',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'interestFee',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'caps',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled: !!liability,
    },
  })

  // Fetch collateral vault stats for supply APY and supply cap
  const { data: collateralStats, isLoading: isCollateralStatsLoading } = useReadContracts({
    contracts: [
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'totalAssets',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'totalBorrows',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'interestRate',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'interestFee',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'caps',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled: !!collateral,
    },
  })

  // Fetch maxDeposit separately (matches working eLend pattern — avoids `as any` batch issues)
  // Use address(0) when user is not connected to check general deposit availability
  const { data: collateralMaxDepositRaw } = useReadContract({
    address: collateral,
    abi: eVaultImplementationAbi,
    functionName: 'maxDeposit',
    args: [userAddress || '0x0000000000000000000000000000000000000000'],
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!collateral,
    },
  })

  const collateralDecimals = collateralTokenInfo?.decimals ?? 18
  const liabilityDecimals = liabilityTokenInfo?.decimals ?? 18

  const { priceUSD: oraclePrice, isLoading: isCollateralPriceLoading } = useAssetPrice({
    assetAddress: collateralUnderlying,
    vaultAddress: collateral,
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: !!collateralUnderlying,
  })

  const { priceUSD: liabilityPrice, isLoading: isLiabilityPriceLoading } = useAssetPrice({
    assetAddress: liabilityUnderlying,
    vaultAddress: liability,
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: !!liabilityUnderlying,
  })

  // First, fetch EVC address and check if collateral is enabled for this sub-account
  // This ensures we only show debt related to this specific collateral
  const { data: evcControlsData, isLoading: isEvcControlsLoading } = useReadContracts({
    contracts: [
      // Get EVC address
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'EVC',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled: !!liability && !!positionAddress,
      staleTime: Number.POSITIVE_INFINITY,
    },
  })

  const evcAddressFromQuery = evcControlsData?.[0]?.result as Address | undefined

  // Check if this collateral is enabled in the sub-account
  const { data: collateralsData, isLoading: isCollateralsLoading } = useReadContract({
    address: evcAddressFromQuery,
    abi: eulerVaultConnectorAbi,
    functionName: 'getCollaterals',
    args: positionAddress ? [positionAddress] : undefined,
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!evcAddressFromQuery && !!positionAddress,
      staleTime: 30_000,
    },
  })

  const collateralsList = (collateralsData as Address[] | undefined) || []
  const isCollateralEnabled = collateralsList.some((c) => c.toLowerCase() === collateral.toLowerCase())

  // Fetch user position data - use positionAddress (sub-account) if user has a position there
  const { data: userPositionData, isLoading: isUserPositionLoading } = useReadContracts({
    contracts: [
      // User's collateral vault balance (shares deposited) - on sub-account
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'balanceOf',
        args: [positionAddress as Address],
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      // User's debt owed on the liability vault - on sub-account
      {
        address: liability,
        abi: eVaultImplementationAbi,
        functionName: 'debtOf',
        args: [positionAddress as Address],
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      // Get convertToAssets to calculate share price
      {
        address: collateral,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets',
        args: [BigInt(10 ** 18)], // 1 share
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled:
        !!positionAddress &&
        !!collateral &&
        !!liability &&
        !isSubAccountLoading &&
        !isEvcControlsLoading &&
        !isCollateralsLoading,
      refetchInterval: 30_000, // Refresh every 30 seconds
    },
  })

  // Parse user position data
  const userCollateralShares = userPositionData?.[0]?.result as bigint | undefined
  const userDebtOwed = userPositionData?.[1]?.result as bigint | undefined
  const sharePrice = userPositionData?.[2]?.result as bigint | undefined

  // Calculate actual collateral assets from shares (same approach as useEBorrowPositions)
  const userCollateralAssets =
    userCollateralShares !== undefined && sharePrice !== undefined
      ? (userCollateralShares * sharePrice) / BigInt(10 ** 18)
      : undefined

  // LTVFull returns: borrowLTV (uint16), liquidationLTV (uint16), initialLiquidationLTV (uint16), targetTimestamp (uint48), rampDuration (uint32)
  // wagmi returns small integers as numbers, not bigints
  const ltvConfig = ltvInfo as readonly [number, number, number, bigint, number] | undefined

  // Liability vault stats
  const liabilityTotalAssets = liabilityStats?.[0]?.result as bigint | undefined
  const liabilityTotalBorrows = liabilityStats?.[1]?.result as bigint | undefined
  const liabilityInterestRate = liabilityStats?.[2]?.result as bigint | undefined
  const liabilityCapsData = liabilityStats?.[4]?.result as [number, number] | undefined

  // Process borrow cap
  const borrowCapRaw = decodeEulerCapToBigInt(liabilityCapsData?.[1])

  // Collateral vault stats
  const collateralTotalAssets = collateralStats?.[0]?.result as bigint | undefined
  const collateralTotalBorrows = collateralStats?.[1]?.result as bigint | undefined
  const collateralInterestRate = collateralStats?.[2]?.result as bigint | undefined
  const collateralInterestFee = collateralStats?.[3]?.result as bigint | undefined
  const collateralCapsData = collateralStats?.[4]?.result as [number, number] | undefined

  // Decode collateral supply cap
  const collateralSupplyCapRaw = decodeEulerCapToBigInt(collateralCapsData?.[0])

  // Calculate APYs using compound interest formula
  // Interest rate is per-second and needs to be compounded over a year
  const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

  // Liability vault (the asset being borrowed) - Borrow APY
  const liabilityRatePerSecond = liabilityInterestRate ? Number(formatUnits(liabilityInterestRate, 27)) : 0
  const borrowAPY = liabilityInterestRate ? ((1 + liabilityRatePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : undefined

  // Calculate utilization for liability vault
  const liabilityUtilization =
    liabilityTotalAssets && liabilityTotalBorrows && liabilityTotalAssets > 0n
      ? Number(liabilityTotalBorrows) / Number(liabilityTotalAssets)
      : 0

  // Liability vault Supply APY - what suppliers of the borrowed asset earn
  // This is more relevant for borrow pairs where collateral is escrow vault
  const liabilityInterestFee = liabilityStats?.[3]?.result as bigint | undefined
  const liabilityFeePercent = liabilityInterestFee ? Number(liabilityInterestFee) / 10000 : 0
  const liabilitySupplyAPY = borrowAPY ? borrowAPY * liabilityUtilization * (1 - liabilityFeePercent) : 0

  // Collateral vault Supply APY (for when collateral can be borrowed by others)
  const collateralUtilization =
    collateralTotalAssets && collateralTotalBorrows && collateralTotalAssets > 0n
      ? Number(collateralTotalBorrows) / Number(collateralTotalAssets)
      : 0
  const collateralRatePerSecond = collateralInterestRate ? Number(formatUnits(collateralInterestRate, 27)) : 0
  const collateralBorrowAPY = collateralInterestRate ? ((1 + collateralRatePerSecond) ** SECONDS_PER_YEAR - 1) * 100 : 0
  const collateralFeePercent = collateralInterestFee ? Number(collateralInterestFee) / 10000 : 0
  const collateralBaseSupplyAPY = collateralBorrowAPY * collateralUtilization * (1 - collateralFeePercent)

  // Get native APY for the collateral asset (intrinsic yield for PT tokens, staked ETH, etc.)
  const collateralNativeApyData = getNativeApyForAsset(nativeApysData, collateralUnderlying)
  const collateralNativeApy = collateralNativeApyData?.apy || 0

  // Total collateral supply APY = base lending APY + native APY (simple addition)
  const collateralSupplyAPY = collateralBaseSupplyAPY + collateralNativeApy

  // No fallback to liabilitySupplyAPY — that represents lender yield on the borrow
  // vault, not what the borrower earns. Escrow collateral correctly shows 0%.
  const supplyAPY = collateralSupplyAPY

  // Calculate utilization percentage for display
  const utilization = liabilityUtilization * 100

  const collateralSymbol = collateralTokenInfo?.symbol
  const liabilitySymbol = liabilityTokenInfo?.symbol
  const isCorrelated = isCorrelatedAssets(collateralSymbol, liabilitySymbol)

  // Calculate formatted values for display
  const collateralTotalSupplyUSD =
    collateralTotalAssets && oraclePrice
      ? Number(formatUnits(collateralTotalAssets, collateralDecimals)) * oraclePrice
      : undefined

  // Calculate liability amounts in tokens
  const liabilityTotalSupplyTokens = liabilityTotalAssets
    ? Number(formatUnits(liabilityTotalAssets, liabilityDecimals))
    : undefined

  const liabilityTotalBorrowedTokens = liabilityTotalBorrows
    ? Number(formatUnits(liabilityTotalBorrows, liabilityDecimals))
    : undefined

  const liabilityAvailableLiquidity =
    liabilityTotalAssets !== undefined && liabilityTotalBorrows !== undefined
      ? liabilityTotalAssets - liabilityTotalBorrows
      : undefined

  const liabilityAvailableLiquidityTokens = liabilityAvailableLiquidity
    ? Number(formatUnits(liabilityAvailableLiquidity, liabilityDecimals))
    : undefined

  // Convert liability amounts to USD using liability price
  const liabilityTotalSupplyUSD =
    liabilityTotalSupplyTokens !== undefined && liabilityPrice > 0
      ? liabilityTotalSupplyTokens * liabilityPrice
      : undefined

  const liabilityTotalBorrowedUSD =
    liabilityTotalBorrowedTokens !== undefined && liabilityPrice > 0
      ? liabilityTotalBorrowedTokens * liabilityPrice
      : undefined

  const liabilityAvailableLiquidityUSD =
    liabilityAvailableLiquidityTokens !== undefined && liabilityPrice > 0
      ? liabilityAvailableLiquidityTokens * liabilityPrice
      : undefined

  // Calculate Max Multiplier using Euler's formula:
  // result = (10000 * LTV) / (10000 - LTV) - 200 + 10000
  // maxMultiplier = result / 10000
  // This matches the selectMaxLeverageForTwoDifferentAssets selector
  const borrowLTV = ltvConfig?.[0] // borrowLTV in basis points (e.g., 9000 = 90%)
  const liquidationLTVValue = ltvConfig?.[1] // liquidationLTV in basis points
  const maxMultiplier = (() => {
    if (!borrowLTV || borrowLTV === 0) return undefined
    const ltvBps = Number(borrowLTV)
    if (ltvBps >= 10000) return undefined // Invalid LTV >= 100%
    const result = (10000 * ltvBps) / (10000 - ltvBps) - 200 + 10000
    return result / 10000
  })()

  // Calculate Max ROE (Return on Equity)
  // Formula from Euler: totalSupplyApy + (maxLeverageValue - 1) * netApy
  // where netApy = totalSupplyApy - totalBorrowApy
  const netAPY = supplyAPY !== undefined && borrowAPY !== undefined ? supplyAPY - borrowAPY : undefined
  const maxROE =
    supplyAPY !== undefined && maxMultiplier !== undefined && netAPY !== undefined
      ? supplyAPY + (maxMultiplier - 1) * netAPY
      : undefined

  // Get market for both collateral and liability vaults (indexer-first)
  const collateralMarket = getVaultMarket(collateral, indexerVaults?.get(collateral)?.products)
  const liabilityMarket = getVaultMarket(liability, indexerVaults?.get(liability)?.products)

  // Get curator names from governor addresses with fallback to market entities
  const entities = getEntities()
  const collateralCuratorName = collateralGovernor
    ? selectGovernorsFromAddresses(collateralGovernor, entities).governors[0]?.name
    : undefined
  const liabilityCuratorName = liabilityGovernor
    ? selectGovernorsFromAddresses(liabilityGovernor, entities).governors[0]?.name
    : undefined

  return {
    data: {
      collateral,
      liability,
      collateralUnderlying,
      liabilityUnderlying,
      collateralName: collateralTokenInfo?.name,
      collateralSymbol,
      collateralDecimals,
      collateralTokenInfo,
      collateralCuratorName,
      collateralMarket,
      liabilityName: liabilityTokenInfo?.name,
      liabilitySymbol,
      liabilityDecimals,
      liabilityTokenInfo,
      liabilityCuratorName,
      liabilityMarket,
      maxLTV: borrowLTV ? BigInt(borrowLTV) : undefined, // borrowLTV in basis points
      liquidationLTV: liquidationLTVValue ? BigInt(liquidationLTVValue) : undefined, // liquidationLTV in basis points
      borrowAPY,
      supplyAPY,
      utilization,
      isCorrelated,
      oraclePrice,
      oracle,
      unitOfAccount,
      evcAddress,
      maxMultiplier,
      maxROE,
      // Collateral vault stats
      collateralStats: {
        totalAssets: collateralTotalAssets,
        totalBorrows: collateralTotalBorrows,
        totalSupplyUSD: collateralTotalSupplyUSD,
        supplyAPY: collateralSupplyAPY,
        lendingAPY: collateralBaseSupplyAPY,
        intrinsicAPY: collateralNativeApy > 0 ? collateralNativeApy : undefined,
        utilization: collateralUtilization * 100,
        canBeBorrowed: collateralTotalBorrows !== undefined && collateralTotalBorrows > 0n,
        supplyCap:
          collateralSupplyCapRaw !== undefined
            ? NormalizedUnitNumber(formatUnits(collateralSupplyCapRaw, collateralDecimals))
            : undefined,
        supplyCapRaw: collateralSupplyCapRaw,
        maxDeposit:
          collateralMaxDepositRaw !== undefined && collateralMaxDepositRaw !== null
            ? NormalizedUnitNumber(formatUnits(collateralMaxDepositRaw, collateralDecimals))
            : undefined,
      },
      // Liability vault stats
      liabilityStats: {
        totalAssets: liabilityTotalAssets,
        totalBorrows: liabilityTotalBorrows,
        totalBorrowsNormalized: liabilityTotalBorrows
          ? NormalizedUnitNumber(formatUnits(liabilityTotalBorrows, liabilityDecimals))
          : NormalizedUnitNumber('0'),
        totalSupplyUSD: liabilityTotalSupplyUSD,
        totalSupplyTokens: liabilityTotalSupplyTokens,
        totalBorrowedUSD: liabilityTotalBorrowedUSD,
        totalBorrowedTokens: liabilityTotalBorrowedTokens,
        availableLiquidity: liabilityAvailableLiquidity,
        availableLiquidityUSD: liabilityAvailableLiquidityUSD,
        availableLiquidityTokens: liabilityAvailableLiquidityTokens,
        availableLiquidityNormalized: liabilityAvailableLiquidity
          ? NormalizedUnitNumber(formatUnits(liabilityAvailableLiquidity, liabilityDecimals))
          : NormalizedUnitNumber('0'),
        borrowCap: borrowCapRaw ? NormalizedUnitNumber(formatUnits(borrowCapRaw, liabilityDecimals)) : undefined,
        supplyAPY: liabilitySupplyAPY,
        borrowAPY,
        utilization: liabilityUtilization * 100,
        priceUSD: liabilityPrice,
      },
      vaultData: {
        vault: liability,
        collateralLTVInfo: borrowLTV
          ? [
              {
                collateral,
                borrowLtvRaw: BigInt(borrowLTV),
                liquidationLtvRaw: BigInt(liquidationLTVValue || 0),
              },
            ]
          : [],
        irmInfo: {
          interestRateInfo: [
            {
              cash: (liabilityTotalAssets || 0n) - (liabilityTotalBorrows || 0n),
              borrows: liabilityTotalBorrows || 0n,
              borrowAPY: liabilityInterestRate || 0n,
              supplyAPY: 0n,
            },
          ],
        },
      },
      // User position data
      userPosition: userAddress
        ? {
            hasPosition:
              userCollateralAssets !== undefined &&
              userCollateralAssets > 0n &&
              userDebtOwed !== undefined &&
              userDebtOwed > 0n &&
              isCollateralEnabled, // Must have collateral enabled in sub-account
            subAccountId: activeSubAccountId, // Include the active sub-account ID
            collateralShares: userCollateralShares,
            collateralAssets: userCollateralAssets,
            // This pair's collateral amount (for display in the pair details)
            collateralAssetsFormatted: userCollateralAssets
              ? Number(formatUnits(userCollateralAssets, collateralDecimals))
              : 0,
            // This pair's collateral value in USD (for display)
            collateralValueUSD:
              userCollateralAssets && oraclePrice
                ? Number(formatUnits(userCollateralAssets, collateralDecimals)) * oraclePrice
                : 0,
            debtOwed: userDebtOwed,
            debtOwedFormatted: userDebtOwed ? Number(formatUnits(userDebtOwed, liabilityDecimals)) : 0,
            // Debt in USD = debt amount * liability price
            debtOwedUSD: userDebtOwed ? Number(formatUnits(userDebtOwed, liabilityDecimals)) * liabilityPrice : 0,
            // ACCOUNT-WIDE LTV from Euler Account Lens (considers ALL enabled collaterals)
            // Falls back to pair-specific calculation if lens data unavailable
            currentLTV:
              accountLiquidity?.currentLTV ??
              (() => {
                if (!userCollateralAssets || userCollateralAssets === 0n || !oraclePrice) return 0
                if (!userDebtOwed || userDebtOwed === 0n) return 0
                const collateralValueUSD = Number(formatUnits(userCollateralAssets, collateralDecimals)) * oraclePrice
                const debtValueUSD = Number(formatUnits(userDebtOwed, liabilityDecimals)) * liabilityPrice
                return collateralValueUSD > 0 ? (debtValueUSD / collateralValueUSD) * 100 : 0
              })(),
            // ACCOUNT-WIDE Health Factor from Euler Account Lens (considers ALL enabled collaterals)
            // This is the true health factor used by Euler for liquidation decisions
            // Falls back to pair-specific calculation if lens data unavailable
            healthFactor:
              accountLiquidity?.healthFactor ??
              (() => {
                if (!userCollateralAssets || userCollateralAssets === 0n || !oraclePrice) return undefined
                if (!userDebtOwed || userDebtOwed === 0n) return undefined // No debt = infinite health
                if (!liquidationLTVValue) return undefined // No liquidation LTV data

                const collateralValueUSD = Number(formatUnits(userCollateralAssets, collateralDecimals)) * oraclePrice
                const debtValueUSD = Number(formatUnits(userDebtOwed, liabilityDecimals)) * liabilityPrice

                // liquidationLTVValue is in basis points (e.g., 9500 = 95%)
                const liquidationLTV = liquidationLTVValue / 10000

                // Health factor = (collateral × liquidation LTV) / debt
                const collateralValueLiquidation = collateralValueUSD * liquidationLTV
                return collateralValueLiquidation / debtValueUSD
              })(),
            // Liquidation threshold (from liquidation LTV)
            liquidationThreshold: liquidationLTVValue ? liquidationLTVValue / 100 : undefined,
            // Account-wide values from the lens (considering ALL enabled collaterals)
            accountTotalCollateralUSD: accountLiquidity?.totalCollateralValueUSD,
            accountTotalDebtUSD: accountLiquidity?.totalDebtValueUSD,
            accountNetValueUSD: accountLiquidity?.netValueUSD,
            // Effective max LTV weighted by all collaterals' individual max LTVs
            // Use this instead of pair-specific maxLTV for multi-collateral positions
            accountEffectiveMaxLTV: accountLiquidity?.effectiveMaxLTV,
            // Effective liquidation LTV weighted by all collaterals' individual liquidation LTVs
            accountEffectiveLiquidationLTV: accountLiquidity?.effectiveLiquidationLTV,
            // Max borrow capacity in USD (collateral value weighted by borrow LTVs)
            accountMaxBorrowCapacityUSD: accountLiquidity?.maxBorrowCapacityUSD,
            // Raw liquidity data for advanced use cases
            accountLiquidityRaw: accountLiquidity?.raw,
          }
        : undefined,
    },
    // Only show loading on initial load (when no essential data), not on refetches
    isLoading:
      !vaultAssetsData &&
      (isAssetsLoading ||
        isTokenLoading ||
        isLtvLoading ||
        isLiabilityStatsLoading ||
        isCollateralStatsLoading ||
        isCollateralPriceLoading ||
        isLiabilityPriceLoading ||
        isNativeApyLoading),
    isPositionLoading:
      isUserPositionLoading ||
      isSubAccountLoading ||
      isEvcControlsLoading ||
      isCollateralsLoading ||
      isAccountLiquidityLoading,
  }
}
